<script setup lang="ts">
/* v8 ignore file */
import { consola } from 'consola';
import { onMounted, onBeforeUnmount } from 'vue';
import { Capacitor } from '@capacitor/core';

import { useUserProfileStore } from '@entities/user'
import { useAuthStore } from '~/features/auth';
import { useModalStore } from '@shared/model';
import { useInstitutionStore } from '@entities/institution';
import { useOnboardingTrigger } from '~/features/onboarding/@x/auth';
import { usePushNotificationSettings } from '~/features/notifications/@x/auth';

const storeModal = useModalStore();
const storeAuth = useAuthStore();
const storeUserProfile = useUserProfileStore();
const modalStore = useModalStore();
const storeInstitution = useInstitutionStore();
const { startOnboarding } = useOnboardingTrigger();
const { clearOnboardingPushPending } = usePushNotificationSettings();
const hasClickedTelegramLink = ref(false);

const logger = consola.withTag('NumberConfirmation');

const { t } = useI18n();
const loader = ref(false);
const errorMessage = ref(null);
const successMessage = ref(null);

const isCheckPhoneVerified = ref(false);

const handleTelegramClick = () => {
  hasClickedTelegramLink.value = true;

  // Автоматически открываем Telegram через небольшую задержку
  setTimeout(() => {
    openTelegram()
  }, 500)
};

// TODO вынести функионал в composables
// Функция для открытия Telegram
const openTelegram = () => {
  try {
    // Проверяем, находимся ли мы в мобильном приложении
    const isInApp = Capacitor.getPlatform() !== 'web'

    logger.log('Opening Telegram URL:', telegramLink.value)
    logger.log('Is in app:', isInApp)

    // На Android App.openUrl() не реализован, поэтому используем только window.open
    if (isInApp) {
      logger.log('Using window.open for mobile app (App.openUrl not implemented on Android)')
    }

    // Пытаемся открыть Telegram через window.open
    logger.log('Using window.open to open URL')
    const popup = window.open(telegramLink.value, '_blank')

    // Если popup заблокирован, показываем инструкцию
    if (!popup || popup.closed || popup.closed === undefined) {
      logger.log('Popup blocked, showing instruction')
    } else {
      logger.log('Telegram opened successfully')
      hasClickedTelegramLink.value = true;
      // НЕ закрываем приложение при авторизации через Telegram
    }

  } catch (error) {
    logger.error('Ошибка при открытии Telegram:', error)
  }
}


const userPhone = computed(() => modalStore.modalData.userData?.phone || storeUserProfile.getUser?.default_phone_number || '...');
// Computed для описания и ссылки
const description = computed(() =>
  t('modal.auth_modal_state.number_confirmation.description', {
    phone: userPhone.value,
  }),
);

const telegramLink = computed(() =>
  userPhone.value === '...'
    ? '#'
    : `https://t.me/${storeInstitution.institution?.telegram_name}?start=${userPhone.value.replaceAll(/\D/g, '')}-ru-web-${storeInstitution.institution?.id}`,
);


const checkPhoneVerified = () => {
  // Показываем индикатор загрузки (прогресс-бар сверху + спиннер на кнопке),
  // пока идёт проверка — иначе у пользователя нет никакой обратной связи на клик.
  loader.value = true;
  errorMessage.value = null;
  storeAuth
    .checkPhoneVerification({ phoneNumber: userPhone.value, institutionId: storeInstitution.institution?.id})
    .then(() => {
      setTimeout(async () => {
        // Телефон подтверждён — показываем онбординг новичку (благодарность + баллы +
        // предложение сохранить адрес). Если уже показывали — просто закрываем модалку.
        // Индикатор держим до самого показа онбординга, чтобы не было «мёртвой» паузы.
        if (!(await startOnboarding())) {
          storeModal.close();
        }
        loader.value = false;
      }, 1000);
    })

    .catch((error) => {
      isCheckPhoneVerified.value = true;
      errorMessage.value = error?.data?.message ?? 'Не удалось проверить подтверждение. Попробуйте ещё раз.';
      loader.value = false;
      setTimeout(() => {
        errorMessage.value = null;
        successMessage.value = null;
      }, 6000);
    });
};

// Автоматически открываем Telegram при загрузке компонента
onMounted(() => {
  // Открываем Telegram через 1 секунду после загрузки компонента
  setTimeout(() => {
    if (!hasClickedTelegramLink.value) {
      openTelegram()
    }
  }, 1000)
})

// Если пользователь закрыл подтверждение, так и не подтвердив телефон, — снимаем
// отложенный пуш-промпт, чтобы он не всплыл при закрытии случайной модалки позже.
// При успешном подтверждении к моменту размонтирования телефон уже verified — флаг не трогаем.
onBeforeUnmount(() => {
  if (!storeUserProfile.getUser?.is_phone_verified) {
    clearOnboardingPushPending();
  }
})
</script>

<template>
  <client-only>
    <div class="modelReg">
      <div class="modelReg__grid">
        <!-- Прогресс-бар загрузки сверху: обратная связь на нажатие «Проверить» -->
        <div v-if="loader" class="modelReg__alertLoading"></div>
        <div class="modelReg__title">
          <h3>{{ $t('modal.auth_modal_state.number_confirmation.title') }}</h3>
          <p v-html="description"></p>

          <!-- Информационное сообщение -->
          <div class="mb-4 p-3 bg-blue-50 rounded-lg">
            <div class="flex items-start gap-2">
              <UIcon name="i-heroicons-information-circle" class="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p class="text-sm text-blue-700">
                  Telegram откроется автоматически через несколько секунд
                </p>
              </div>
            </div>
          </div>

          <NuxtLink :to="telegramLink" target="_blank" class="flex items-center gap-2 px-4 py-3 bg-black text-white rounded-full" @click="handleTelegramClick">
            <UIcon name="file-icons:telegram" class="w-8 h-8" />
           <span>{{ $t('modal.auth_modal_state.number_confirmation.link_to_telegram') }}</span>
          </NuxtLink>
        </div>

        <p v-if="!storeUserProfile.getUser.is_phone_verified" class="text-red-500 text-center mb-3">
          {{ errorMessage }}
        </p>

        <UiButtonMain
          v-if="hasClickedTelegramLink"
          class="btn"
          color="red"
          size="xl"
          :label="$t('modal.auth_modal_state.number_confirmation.btn_done')"
          block
          type="submit"
          :loading="loader"
          :disabled="loader"
          @click="checkPhoneVerified"
        />
      </div>
    </div>
  </client-only>
</template>

<style scoped lang="scss"></style>

